// ============================================================================
// TrimaRankingCard — A single sortable card in the ranking list
// ============================================================================
// Wired to @dnd-kit/sortable's `useSortable` hook. Opacity is interpolated
// across the 5 positions (top = 1.0, bottom = 0.7) to subtly suggest hierarchy
// without using color, which would imply "good/bad" semantics we don't want.
// ============================================================================

import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getLocaleValue } from 'helpers/utils';
import { useTranslation } from 'react-i18next';
import { TrimaRankingAnswer } from 'pages/questionnaire-trima/types/trima.types';
import { TrimaRankingCardWrapper } from 'pages/questionnaire-trima/slides/ranking/TrimaRankingCard.styled';

export interface TrimaRankingCardProps {
  answer: TrimaRankingAnswer;
  /** Position index in the current list (0 = top). */
  position: number;
  /** Total number of cards, used to compute the opacity gradient. */
  total: number;
}

/**
 * Linear opacity gradient: top card = 1.0, bottom card = 0.7.
 */
const computeOpacity = (position: number, total: number): number => {
  if (total <= 1) return 1;
  const min = 0.7;
  const max = 1.0;
  const t = position / (total - 1);
  return max - (max - min) * t;
};

const TrimaRankingCard: React.FC<TrimaRankingCardProps> = ({ answer, position, total }) => {
  const { i18n } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: answer.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <TrimaRankingCardWrapper $isDragging={isDragging} $opacity={computeOpacity(position, total)}>
        <span className="trima-handle" aria-hidden="true">⋮⋮</span>
        <span className="trima-card-label">{getLocaleValue(answer.label, i18n.language)}</span>
      </TrimaRankingCardWrapper>
    </div>
  );
};

export default TrimaRankingCard;
